import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageItemType, Playlist, TransitionType } from '../types';
import { Page } from './Page';
import { ThemeWrapper } from './ThemeWrapper';
import { ForceDisconnectButton } from './ForceDisconnectButton';
import { DeviceIdDialog } from './DeviceIdDialog';
import { PlaybackProvider } from './PlaybackContext';
import { reportPlaybackStall, trackEvent } from '@/lib/errorReporting';

interface PlayListProps {
  playlist: Playlist;
}

const PLAY_FULL_VIDEO_DURATION = 0;
// Backstop for a "play the whole video" page whose `ended` event never arrives.
// Comfortably longer than any realistic signage clip, so a healthy video is
// never cut short.
const MAX_VIDEO_PAGE_MS = 30 * 60 * 1000;
// Separate, much shorter backstop for a video that never STARTS, as opposed to
// one that runs long. Cancelled the moment frames are really playing, so a
// legitimately long clip is never cut short by it.
//
// 30 minutes was the only safety net here, and it is far too slow to matter:
// a reception screen blank for half an hour is a complaint, not a recovery.
const VIDEO_START_TIMEOUT_MS = 45 * 1000;
// A playlist with ONE item has nowhere to advance to, so the only recovery
// available is to rebuild the page and try the same video again.
//
// This is display 21 "Entré": a single video page, which never transitions and
// therefore never re-fetches. When its one video fails there is no next page,
// no `ended`, and nothing else to show — it stays broken until someone presses
// "Starta om skärm". Slow on purpose: the media host being unreachable is not
// improved by asking every two seconds.
const SINGLE_PAGE_RETRY_MS = 30 * 1000;
function PlayList({ playlist }: PlayListProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [theme, setTheme] = useState(playlist?.playlistItems?.[currentIndex]?.page?.theme?.css);
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const startTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const activeIndexRef = useRef(0);
  // Bumped to force the active page to remount, which is the only way to make
  // a <video> fetch its source again when there is no other page to move to.
  const [pageGeneration, setPageGeneration] = useState(0);

  const sortedPlaylistItems = useMemo(
    () => [...(playlist?.playlistItems ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [playlist?.playlistItems],
  );
  const playlistLength = sortedPlaylistItems.length;
  const currentPlaylistItem = sortedPlaylistItems?.[currentIndex];
  const nextIndex = currentIndex === playlistLength - 1 ? 0 : currentIndex + 1;
  const nextPlaylistItem = sortedPlaylistItems?.[nextIndex];
  const currentHasVideo =
    currentPlaylistItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false;
  const nextHasVideo =
    nextPlaylistItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false;

  const transitionIn = sortedPlaylistItems?.[currentIndex]?.transitionIn ?? TransitionType.Ease;
  const useOpacityTransition = transitionIn !== 'linear' && !currentHasVideo && !nextHasVideo;
  const useFullVideoDuration =
    currentHasVideo && currentPlaylistItem?.duration === PLAY_FULL_VIDEO_DURATION && playlistLength > 1;

  const clearAdvanceTimeout = useCallback(() => {
    clearTimeout(advanceTimeoutRef.current);
  }, []);

  const clearStartTimeout = useCallback(() => {
    clearTimeout(startTimeoutRef.current);
  }, []);

  const clearRetryTimeout = useCallback(() => {
    clearTimeout(retryTimeoutRef.current);
  }, []);

  const moveToIndex = useCallback(
    (fromIndex: number, toIndex: number) => {
      const currentItem = sortedPlaylistItems[fromIndex];
      const nextItem = sortedPlaylistItems[toIndex];
      const nextTheme = nextItem?.page?.theme?.css ?? undefined;
      const transitionHasVideo =
        (currentItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false) ||
        (nextItem?.page?.pageItems?.some(pageItem => pageItem.type === PageItemType.Video) ?? false);
      const themeDelay = transitionHasVideo || currentItem?.transitionOut === 'linear' ? 0 : 50;

      setCurrentIndex(toIndex);

      // Page transitions are the heartbeat of a playlist. As breadcrumbs they
      // show, on any later report, whether the screen was still cycling.
      trackEvent('playlist', 'page advance', {
        fromIndex,
        toIndex,
        fromPageId: currentItem?.pageId,
        toPageId: nextItem?.pageId,
      });

      if (nextItem?.page?.theme?.themeId !== currentItem?.page?.theme?.themeId) {
        setTimeout(() => {
          setTheme(nextTheme);
        }, themeDelay);
      }
    },
    [sortedPlaylistItems],
  );

  const moveToNextFrom = useCallback(
    (fromIndex: number) => {
      if (playlistLength === 0 || fromIndex !== activeIndexRef.current) {
        return;
      }

      const toIndex = fromIndex === playlistLength - 1 ? 0 : fromIndex + 1;
      moveToIndex(fromIndex, toIndex);
    },
    [moveToIndex, playlistLength],
  );

  const handleVideoEnded = useCallback(() => {
    if (!useFullVideoDuration) {
      return;
    }

    clearAdvanceTimeout();
    moveToNextFrom(currentIndex);
  }, [clearAdvanceTimeout, currentIndex, moveToNextFrom, useFullVideoDuration]);

  // The incoming video is really rendering, so the page is alive and the start
  // watchdog is no longer needed.
  const handleVideoPlaying = useCallback(() => {
    clearStartTimeout();
  }, [clearStartTimeout]);

  /**
   * The video cannot be played and retrying is exhausted.
   *
   * Only "play the whole video" pages are stuck by this — they advance solely
   * on `ended`, which a dead video never fires. A timed page still has its own
   * timer, so cutting it short here would just hide the failure faster.
   */
  const handleVideoFailed = useCallback(() => {
    clearStartTimeout();

    // Nowhere to advance to. Rebuild the page instead, so the video is fetched
    // again from scratch and the screen heals itself once the media host is
    // reachable — rather than waiting for someone to press "Starta om skärm".
    if (playlistLength <= 1) {
      reportPlaybackStall('retrying the only page after a video failure', {
        playlistId: playlist?.playlistId,
        pageId: currentPlaylistItem?.pageId,
        retryInMs: SINGLE_PAGE_RETRY_MS,
      });

      clearRetryTimeout();
      retryTimeoutRef.current = setTimeout(() => {
        setPageGeneration(generation => generation + 1);
      }, SINGLE_PAGE_RETRY_MS);
      return;
    }

    if (!useFullVideoDuration) {
      return;
    }

    reportPlaybackStall('advancing past a video that could not be played', {
      playlistId: playlist?.playlistId,
      pageId: currentPlaylistItem?.pageId,
      index: currentIndex,
    });

    clearAdvanceTimeout();
    moveToNextFrom(currentIndex);
  }, [
    clearAdvanceTimeout,
    clearRetryTimeout,
    clearStartTimeout,
    currentIndex,
    currentPlaylistItem?.pageId,
    moveToNextFrom,
    playlist?.playlistId,
    playlistLength,
    useFullVideoDuration,
  ]);

  const playbackValue = useMemo(() => ({
    useFullVideoDuration,
    onVideoEnded: handleVideoEnded,
    onVideoFailed: handleVideoFailed,
    onVideoPlaying: handleVideoPlaying,
  }), [useFullVideoDuration, handleVideoEnded, handleVideoFailed, handleVideoPlaying]);

  useEffect(() => {
    activeIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    setCurrentIndex(0);
    setTheme(sortedPlaylistItems[0]?.page?.theme?.css);
    activeIndexRef.current = 0;
    clearAdvanceTimeout();
    // A new playlist is a fresh start, so a pending retry from the old one
    // must not fire and remount a page that is no longer on screen.
    clearRetryTimeout();
  }, [clearAdvanceTimeout, clearRetryTimeout, playlist?.playlistId, sortedPlaylistItems]);

  useEffect(() => {
    if (playlistLength === 0) {
      clearAdvanceTimeout();
      return;
    }

    clearAdvanceTimeout();

    if (useFullVideoDuration) {
      // In this mode the ONLY way to advance is the video's `ended` event, and
      // a video that dies never fires it. Two separate failures need catching,
      // on very different timescales.
      //
      // A video that never STARTS: caught in seconds. This is the one seen in
      // production — a morning power-on raced the network, the load failed,
      // and the screen stayed blank until someone restarted it by hand.
      // Cancelled as soon as frames are really playing, so it cannot cut a
      // long clip short.
      clearStartTimeout();
      startTimeoutRef.current = setTimeout(() => {
        reportPlaybackStall('video never started playing', {
          playlistId: playlist?.playlistId,
          pageId: currentPlaylistItem?.pageId,
          index: currentIndex,
          limitMs: VIDEO_START_TIMEOUT_MS,
        });

        moveToNextFrom(currentIndex);
      }, VIDEO_START_TIMEOUT_MS);

      // A video that starts and then overruns: the long backstop. This used to
      // report and then do nothing unless recovery was switched on, which no
      // display in the field had. A page stuck for half an hour is not a
      // behaviour worth preserving, so it now advances either way.
      advanceTimeoutRef.current = setTimeout(() => {
        reportPlaybackStall('video page overran without an `ended` event', {
          playlistId: playlist?.playlistId,
          pageId: currentPlaylistItem?.pageId,
          index: currentIndex,
          limitMs: MAX_VIDEO_PAGE_MS,
        });

        moveToNextFrom(currentIndex);
      }, MAX_VIDEO_PAGE_MS);

      return () => {
        clearAdvanceTimeout();
        clearStartTimeout();
      };
    }

    const duration = currentPlaylistItem?.duration && currentPlaylistItem.duration > PLAY_FULL_VIDEO_DURATION
      ? currentPlaylistItem.duration
      : 10;

    advanceTimeoutRef.current = setTimeout(() => {
      moveToNextFrom(currentIndex);
    }, duration * 1000);

    return () => {
      clearAdvanceTimeout();
    };
  }, [
    clearAdvanceTimeout,
    currentIndex,
    currentPlaylistItem?.pageId,
    playlist?.playlistId,
    currentPlaylistItem?.duration,
    moveToNextFrom,
    playlistLength,
    useFullVideoDuration,
  ]);

  return (
    <>
      <div className="w-full h-screen bg-black">
        <PlaybackProvider value={playbackValue}>
          {sortedPlaylistItems?.map((playlistItem, index) => {
            const page = playlistItem?.page;
            const isActivePage = index === currentIndex;
            const wrapperIdentity =
              playlistItem?.playlistItemId !== undefined
                ? playlistItem.playlistItemId.toString()
                : `${playlistItem?.pageId ?? 'page'}_${index}`;
            const wrapperId = `page_wrapper_${wrapperIdentity}`;

            const pageTheme = isActivePage && useOpacityTransition ? theme : page?.theme?.css;
            let resolvedThemeConfig: { theme?: { background?: string } } | null = null;
            try {
              resolvedThemeConfig = (typeof pageTheme === 'string' ? JSON.parse(pageTheme) : pageTheme) as { theme?: { background?: string } } | null;
            } catch {
              // Malformed theme JSON - skip background extraction.
            }
            const themeBackground = resolvedThemeConfig?.theme?.background;
            const pageBackground = themeBackground
              ? themeBackground.startsWith('http') ? `url(${themeBackground})` : themeBackground
              : undefined;

            return (
              <div
                id={wrapperId}
                className={`
                 absolute top-0 left-0 w-full h-full
                 ${isActivePage ? 'opacity-100  z-10' : 'opacity-0 z-0'}
                 ${useOpacityTransition ? 'duration-1000 transition-opacity ' : ' '}
               `}
                style={{ background: pageBackground, backgroundSize: 'cover', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat' }}
                // The generation is only on the ACTIVE page, and only changes
                // when a single-page playlist has to retry a failed video.
                // Including it everywhere would remount pages that are working.
                key={isActivePage ? `${wrapperIdentity}_${pageGeneration}` : wrapperIdentity}
              >
                <ThemeWrapper key={wrapperIdentity} config={pageTheme as any}>
                  {page && (
                    <Page
                      page={page}
                      isActive={isActivePage}
                    />
                  )}
                </ThemeWrapper>
              </div>
            );
          })}
        </PlaybackProvider>
      </div>
      <DeviceIdDialog />
      <ForceDisconnectButton />
    </>
  );
}

export default PlayList;
